/**
 * VehicleDeviceLinkModal
 * Koppla en GPS-enhet till ett specifikt fordon.
 * Bara okopplade enheter (vehicle_id tom, status ≠ VEHICLE). 50/sida, lite-API.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  DevicePhoneMobileIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { BaseModal } from '@/components/common/BaseModal';
import api from '@/services/api/client';
import { useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import type { Vehicle } from '@/services/vehicles/vehiclesService';

interface DeviceOption {
  imei: string;
  model?: string;
  iccid?: string;
  status: string;
  organization_name?: string;
  vehicle_registration?: string;
  vehicle_id?: number;
  /** 1 när enheten hämtas från Deque-lager. */
  platform_stock?: number | string | boolean;
}

interface VehicleDeviceLinkModalProps {
  vehicle: Vehicle;
  onClose: () => void;
  onSuccess: () => void;
}

const PAGE_SIZE = 50;

function isPlatformStock(device: DeviceOption): boolean {
  return Number(device.platform_stock) === 1 || device.platform_stock === true;
}

const STATUS_COLORS: Record<string, string> = {
  STOCK:    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  CUSTOMER: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  VEHICLE:  'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  RETURNED: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
};

const VehicleDeviceLinkModal: React.FC<VehicleDeviceLinkModalProps> = ({ vehicle, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isGlobalAdmin = user?.is_global_admin === true;

  const [search, setSearch] = useState('');
  // Visa bara lediga enheter som standard (ej monterade på annat fordon)
  const [statusFilter, setStatusFilter] = useState<string>('AVAILABLE');
  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedImei, setSelectedImei] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [handleExisting, setHandleExisting] = useState<'SWAP' | 'UNMOUNT'>('UNMOUNT');

  const vehicleName = [vehicle.make, vehicle.model, vehicle.year].filter(Boolean).join(' ') || vehicle.registration_number;

  const fetchDevices = useCallback(async (q: string, status: string, offset: number, append: boolean) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('search', q);
      if (status && status !== 'AVAILABLE') params.set('status', status);
      params.set('onlyWithoutVehicle', 'true');
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));
      params.set('includeSubOrgs', 'true');
      params.set('lite', 'true');
      if (!isGlobalAdmin) params.set('includePlatformStock', 'true');

      const res = await api.get(`/api/device-management/devices?${params}`);
      const all_raw: DeviceOption[] = Array.isArray(res.data.data) ? res.data.data : [];
      const nextTotal = typeof res.data.meta?.total === 'number' ? res.data.meta.total : all_raw.length;

      setTotal(nextTotal);
      setNextOffset(offset + all_raw.length);
      setDevices(prev => (append ? [...prev, ...all_raw] : all_raw));
    } catch {
      if (!append) {
        setDevices([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [isGlobalAdmin]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setDevices([]);
      setTotal(0);
      setNextOffset(0);
      fetchDevices(search, statusFilter, 0, false);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, statusFilter, fetchDevices]);

  const hasMore = nextOffset < total;

  const handleLink = async () => {
    if (!selectedImei) return;
    setSaving(true);
    try {
      await api.post(`/api/device-management/devices/${selectedImei}/move`, {
        targetVehicleId: vehicle.id,
        handleExistingDevice: vehicle.device_imei ? handleExisting : undefined,
        notes: `Kopplad via fordonslistan av ${user?.email ?? 'okänd'}`,
      });
      setDone(true);
    } catch (err: any) {
      const msg = err.response?.data?.error || t('common.saveError');
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    const sel = devices.find(d => d.imei === selectedImei);
    return (
      <BaseModal isOpen onClose={onSuccess} title={t('vehicles.deviceLink.successTitle')} size="md">
        <div className="flex flex-col items-center gap-4 py-6">
          <CheckCircleIcon className="w-16 h-16 text-green-500" />
          <div className="text-center">
            <p className="text-lg font-semibold text-textDefault">
              {t('vehicles.deviceLink.successMessage').replace('{{reg}}', vehicle.registration_number)}
            </p>
            <p className="text-sm text-textSecondary mt-1">
              IMEI: <span className="font-mono">{selectedImei}</span>
              {sel?.model && ` · ${sel.model}`}
            </p>
            <p className="text-xs text-textTertiary mt-1">
              {t('vehicles.deviceLink.successAssigned').replace('{{org}}', vehicle.organization_name ?? '')}
            </p>
          </div>
          <button onClick={onSuccess} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            {t('common.close')}
          </button>
        </div>
      </BaseModal>
    );
  }

  const selected = devices.find(d => d.imei === selectedImei);
  const filteredVisible = devices;

  return (
    <BaseModal
      isOpen
      onClose={onClose}
      title={t('vehicles.deviceLink.modalTitle').replace('{{reg}}', vehicle.registration_number)}
      subtitle={vehicleName}
      size="2xl"
    >
      <div className="space-y-4">
        {/* Varning om fordonet redan har enhet */}
        {vehicle.device_imei && (
          <div className="flex items-start gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-amber-800 dark:text-amber-300">{t('vehicles.deviceLink.existingDeviceWarning')}</span>
              <span className="font-mono text-amber-700 dark:text-amber-400">{vehicle.device_imei}</span>
              <div className="mt-2 flex gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" value="UNMOUNT" checked={handleExisting === 'UNMOUNT'} onChange={() => setHandleExisting('UNMOUNT')} />
                  <span className="text-xs text-textDefault">{t('vehicles.deviceLink.unmountToStock')}</span>
                </label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" value="SWAP" checked={handleExisting === 'SWAP'} onChange={() => setHandleExisting('SWAP')} />
                  <span className="text-xs text-textDefault">{t('vehicles.deviceLink.swap')}</span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Sök + statusfilter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textTertiary pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={e => { setSearch(e.target.value); setSelectedImei(null); }}
              placeholder={t('vehicles.deviceLink.searchPlaceholder')}
              className="w-full pl-9 pr-4 py-2 bg-bgElevated border border-borderDefault rounded-lg text-textDefault text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            {loading && (
              <ArrowPathIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textTertiary animate-spin" />
            )}
          </div>
          <div className="relative">
            <FunnelIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-textTertiary pointer-events-none" />
            <select
              value={statusFilter}
              onChange={e => { setStatusFilter(e.target.value); setSelectedImei(null); }}
              className="pl-8 pr-3 py-2 bg-bgElevated border border-borderDefault rounded-lg text-textDefault text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
            >
              <option value="AVAILABLE">{t('vehicles.deviceLink.filterAvailable')}</option>
              <option value="STOCK">{t('vehicles.deviceLink.filterStock')}</option>
              <option value="CUSTOMER">{t('vehicles.deviceLink.filterCustomer')}</option>
              <option value="RETURNED">{t('vehicles.deviceLink.filterReturned')}</option>
            </select>
          </div>
        </div>

        {/* Info om antal */}
        {!loading && total > 0 && (
          <div className="text-xs text-textTertiary flex flex-wrap gap-x-2">
            <span>
              {filteredVisible.length < total
                ? t('vehicles.deviceLink.countAvailablePaged')
                    .replace('{{shown}}', String(filteredVisible.length))
                    .replace('{{total}}', String(total))
                : t('vehicles.deviceLink.countAvailable').replace('{{count}}', String(total))}
            </span>
            {!isGlobalAdmin && (
              <span className="text-textTertiary">· {t('vehicles.deviceLink.countFilteredOrg')}</span>
            )}
            {vehicle.organization_name && (
              <span className="text-blue-600 dark:text-blue-400">
                · {t('vehicles.deviceLink.assignedToOrg').replace('{{org}}', vehicle.organization_name)}
              </span>
            )}
          </div>
        )}

        {/* Enhetslista */}
        <div className="border border-borderDefault rounded-lg divide-y divide-borderDefault max-h-80 overflow-y-auto">
          {!loading && filteredVisible.length === 0 && (
            <div className="p-6 text-center text-sm text-textSecondary">
              {t('vehicles.deviceLink.noDevicesFound')}
            </div>
          )}
          {loading && devices.length === 0 && (
            <div className="p-6 text-center text-sm text-textSecondary flex items-center justify-center gap-2">
              <ArrowPathIcon className="w-4 h-4 animate-spin" />
              {t('vehicles.deviceLink.loadingDevices')}
            </div>
          )}

          {filteredVisible.map(device => {
            const isCurrentDevice = device.imei === vehicle.device_imei;
            const isSelected = device.imei === selectedImei;
            const isMountedElsewhere = device.status === 'VEHICLE' && !isCurrentDevice;
            const statusLabelKey = `vehicles.deviceLink.status${device.status.charAt(0) + device.status.slice(1).toLowerCase()}` as any;
            const statusLabel = t(statusLabelKey) || device.status;
            const statusColor = STATUS_COLORS[device.status] ?? 'bg-gray-100 text-textSecondary';
            const fromPlatformStock = isPlatformStock(device);

            return (
              <button
                key={device.imei}
                type="button"
                disabled={isCurrentDevice}
                onClick={() => setSelectedImei(isSelected ? null : device.imei)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  isCurrentDevice
                    ? 'opacity-40 cursor-not-allowed bg-bgElevated'
                    : isSelected
                    ? 'bg-blue-50 dark:bg-blue-900/30 border-l-2 border-l-blue-500'
                    : 'hover:bg-bgHover'
                }`}
              >
                <DevicePhoneMobileIcon className={`w-5 h-5 shrink-0 ${isSelected ? 'text-blue-600' : 'text-textTertiary'}`} />

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-semibold text-textDefault">{device.imei}</span>
                    {isCurrentDevice && (
                      <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 px-1.5 py-0.5 rounded font-medium">
                        {t('vehicles.deviceLink.statusCurrent')}
                      </span>
                    )}
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${statusColor}`}>
                      {statusLabel}
                    </span>
                    {fromPlatformStock && (
                      <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 px-1.5 py-0.5 rounded font-medium">
                        {t('vehicles.deviceLink.platformStock')}
                      </span>
                    )}
                    {isMountedElsewhere && device.vehicle_registration && (
                      <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300 px-1.5 py-0.5 rounded">
                        {device.vehicle_registration}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-textSecondary mt-0.5 truncate">
                    {[
                      device.model,
                      device.iccid ? `ICCID: ${device.iccid}` : null,
                      fromPlatformStock ? null : device.organization_name,
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>

                {isSelected && <CheckCircleIcon className="w-5 h-5 text-blue-600 shrink-0" />}
              </button>
            );
          })}
          {hasMore && (
            <button
              type="button"
              onClick={() => fetchDevices(search, statusFilter, nextOffset, true)}
              disabled={loadingMore}
              className="w-full px-4 py-2.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-bgHover disabled:opacity-50"
            >
              {loadingMore ? t('vehicles.deviceLink.loadingDevices') : t('vehicles.deviceLink.loadMore')}
            </button>
          )}
        </div>

        {/* Sammanfattning av vald enhet */}
        {selected && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircleIcon className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-medium text-blue-800 dark:text-blue-300">{t('vehicles.deviceLink.selectedDevice')}</span>
                <span className="font-mono text-blue-700 dark:text-blue-400">{selected.imei}</span>
                {selected.model && <span className="text-blue-600 dark:text-blue-400"> · {selected.model}</span>}
                {selected.status === 'VEHICLE' && selected.vehicle_registration && (
                  <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                    {t('vehicles.deviceLink.deviceMountedElsewhere').replace('{{reg}}', selected.vehicle_registration)}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Knappar */}
        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-bgElevated border border-borderDefault text-textDefault rounded-lg hover:bg-bgHover transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={handleLink}
            disabled={!selectedImei || saving}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {saving && <ArrowPathIcon className="w-4 h-4 animate-spin" />}
            {saving ? t('vehicles.deviceLink.linking') : t('vehicles.deviceLink.linkButton')}
          </button>
        </div>
      </div>
    </BaseModal>
  );
};

export default VehicleDeviceLinkModal;
